/* eslint-disable import/prefer-default-export, no-restricted-imports */
import { colors, createMuiTheme } from '@material-ui/core';
import { formatHex, parse } from 'culori';
import twColors from 'tailwindcss/colors';

import {
  muiButtonLinkRootOverride,
  muiButtonLinkStyleOverrides,
} from './muiButtonLinkStyles';
import {
  muiDialogDefaultProps,
  muiOverlayOverridesV4,
  muiPaperElevation8DataGridPanel,
} from './muiOverlaySurfaces';
import { muiHeaderTypography } from './muiTypography';

function twHex(color) {
  if (typeof color !== 'string') return color;
  if (!color.startsWith('oklch(')) return color;

  const parsed = parse(color);
  if (!parsed) return color;

  return formatHex(parsed);
}

export const muiThemeV4 = createMuiTheme({
  shape: {
    // Matches Tailwind `rounded-xl` (0.75rem) with `html` font-size 14px from CssBaseline below.
    borderRadius: 10.5,
  },
  props: {
    MuiCard: {
      variant: 'outlined',
    },
    MuiButton: {
      disableElevation: true,
      color: 'primary',
    },
    MuiDialog: muiDialogDefaultProps,
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        html: {
          fontSize: 14,
        },
        body: {
          fontSize: '1rem',
        },
        'table.MuiTable-root:has(> tbody.MuiTableBody-root:empty) thead.MuiTableHead-root th.MuiTableCell-head':
          {
            borderBottom: 0,
            borderBottomWidth: 0,
          },
        ':has(.MuiTablePagination-root) table.MuiTable-root:has(> tbody.MuiTableBody-root:empty) thead.MuiTableHead-root th.MuiTableCell-head':
          {
            borderBottom: '1px solid var(--tw-color-gray-200)',
            borderBottomWidth: 1,
          },
        'table.MuiTable-root:not(:has(> tfoot.MuiTableFooter-root)) > tbody.MuiTableBody-root > tr.MuiTableRow-root:last-of-type > :is(td.MuiTableCell-root, th.MuiTableCell-root)':
          {
            borderBottom: 0,
            borderBottomWidth: 0,
          },
        'table.MuiTable-root > tfoot.MuiTableFooter-root > tr.MuiTableRow-root > :is(td.MuiTableCell-root, th.MuiTableCell-root)':
          {
            borderTop: '1px solid var(--tw-color-gray-200)',
          },
      },
    },
    MUIDataTable: {
      outlined: {
        border: '1px solid var(--tw-color-gray-200)',
      },
      paper: {
        boxShadow: 'none',
        '&:not(.MuiPaper-elevation0)': {
          border: '1px solid var(--tw-color-gray-200)',
        },
        '.MuiCard-root &': {
          border: 0,
          borderWidth: 0,
        },
        '.MuiCard-root &[class*="MuiPaper-elevation"]': {
          border: 0,
          borderWidth: 0,
        },
      },
    },
    MuiAccordion: {
      root: {
        borderRadius: 'var(--tw-radius-xl)',
        overflow: 'hidden',
        '&:first-of-type': {
          borderTopLeftRadius: 'var(--tw-radius-xl)',
          borderTopRightRadius: 'var(--tw-radius-xl)',
        },
        '&:last-of-type': {
          borderBottomLeftRadius: 'var(--tw-radius-xl)',
          borderBottomRightRadius: 'var(--tw-radius-xl)',
        },
      },
    },
    MuiCard: {
      root: {
        '.MuiAccordion-root &': {
          border: 0,
          borderWidth: 0,
        },
      },
    },
    MuiContainer: {
      root: {
        // The admin layout (.pcoded-content) already provides horizontal
        // padding on mobile, so drop the Container gutters there to avoid
        // doubling up. Public/auth pages sit outside .pcoded-content and
        // keep their gutters.
        '.pcoded-content &': {
          '@media (max-width: 599.95px)': {
            paddingLeft: 0,
            paddingRight: 0,
          },
        },
      },
    },
    MuiButton: {
      root: {
        textTransform: 'none',
        boxShadow: 'none',
        ...muiButtonLinkRootOverride,
        '&:hover': {
          boxShadow: 'none',
        },
        '&:active': {
          boxShadow: 'none',
        },
        '&:focus': {
          boxShadow: 'none',
        },
      },
      ...muiButtonLinkStyleOverrides,
    },
    MuiDataGrid: {
      root: {
        '.MuiCard-root &': {
          border: 0,
          borderWidth: 0,
        },
      },
    },
    MuiDialogActions: {
      root: {
        justifyContent: 'space-between',
        padding: '0.5rem 1.5rem 1.25rem 1.5rem',
      },
    },
    MuiDivider: {
      root: {
        // Prevent double-line effect when v4 + v5 Divider styles collide.
        border: 0,
        borderBottomWidth: 0,
        height: 1,
        backgroundColor: twHex(twColors.gray[200]),
      },
    },
    MuiToggleButton: {
      root: {
        textTransform: 'none',
      },
    },
    MuiTablePagination: {
      root: {
        borderBottomWidth: 0,
      },
    },
    MuiPaper: {
      outlined: {
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation0: {
        boxShadow: 'none',
      },
      elevation1: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation2: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation3: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation4: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation5: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation6: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation7: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation8: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
        ...muiPaperElevation8DataGridPanel,
      },
      elevation9: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation10: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation11: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation12: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation13: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation14: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation15: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation16: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation17: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation18: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation19: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation20: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation21: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation22: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation23: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
      elevation24: {
        boxShadow: 'none',
        border: '1px solid var(--tw-color-gray-200)',
      },
    },
    MuiInputBase: {
      input: {
        '&::placeholder': {
          opacity: 1,
          color: 'rgba(0, 0, 0, 0.6)',
        },
      },
    },
    MuiLinearProgress: {
      root: {
        borderRadius: 10.5,
        overflow: 'hidden',
      },
      bar: {
        borderRadius: 10.5,
      },
    },
    MuiTabs: {
      indicator: {
        backgroundColor: twHex(twColors.blue[600]),
      },
    },
    MuiTab: {
      root: {
        textTransform: 'none',
        '&$selected': {
          color: twHex(twColors.blue[600]),
        },
      },
    },
    ...muiOverlayOverridesV4,
  },
  palette: {
    type: 'light',
    action: {
      active: 'rgba(0, 0, 0, 0.6)',
    },
    background: {
      default: colors.common.white,
      paper: colors.common.white,
    },
    primary: {
      main: twHex(twColors.blue[600]),
    },
    secondary: {
      main: twHex(twColors.blue[600]),
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
    },
  },
  typography: {
    // Make 1rem == 14px (and keep pxToRem consistent).
    htmlFontSize: 14,
    fontSize: 14,
    fontFamily:
      "'Inter', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Liberation Sans', sans-serif",
    ...muiHeaderTypography,
    p: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: '1.5em',
    },
    overline: {
      fontWeight: 500,
    },
  },
});
